import { useCallback, useEffect, useState } from "react";
import { TKey } from "@/app/translations/TKey";
import { UFile } from "@/app/utils/UFile";
import { ULogger } from "@/app/utils/ULogger";

// Add global types for Google Picker and Google Identity Services
declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

type UseGoogleDrivePickerProps = {
  onFilesSelected: (files: File[]) => void;
  onError?: (errorKey: TKey) => void;
};

export function useGoogleDrivePicker({ onFilesSelected, onError }: UseGoogleDrivePickerProps) {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isPicking, setIsPicking] = useState(false);

  const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || "";
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY || "";

  useEffect(() => {
    // Load Google APIs
    let gapiLoaded = false;
    let gsiLoaded = false;

    const checkLoaded = () => {
      if (gapiLoaded && gsiLoaded) {
        setIsScriptLoaded(true);
      }
    };

    const loadGapi = () => {
      const script = document.createElement("script");
      script.src = "https://apis.google.com/js/api.js";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.gapi.load("picker", () => {
          gapiLoaded = true;
          checkLoaded();
        });
      };
      document.body.appendChild(script);
    };

    const loadGsi = () => {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        gsiLoaded = true;
        checkLoaded();
      };
      document.body.appendChild(script);
    };

    if (!window.gapi) loadGapi();
    else gapiLoaded = true;

    if (!window.google?.accounts) loadGsi();
    else gsiLoaded = true;

    checkLoaded();
  }, []);

  const downloadFile = useCallback(
    async (
      fileId: string,
      fileName: string,
      mimeType: string,
      accessToken: string,
      lastEditedUtc?: number,
    ) => {
      try {
        const response = await fetch(
          `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to download file from Google Drive");
        }

        const blob = await response.blob();

        // Fix missing extension
        let finalName = fileName;
        const currentExt = fileName.split(".").pop()?.toLowerCase();
        const hasValidExt = currentExt && UFile.getCallExtensions().includes(`.${currentExt}`);

        if (!hasValidExt) {
          const ext = UFile.getExtensionFromMimeType(mimeType);
          if (ext) {
            finalName = `${finalName}.${ext}`;
          }
        }

        let fileType = blob.type;
        if (!fileType || fileType === "application/octet-stream") {
          fileType = mimeType;
        }

        const fileOptions: FilePropertyBag = { type: fileType };
        if (lastEditedUtc) {
          fileOptions.lastModified = new Date(lastEditedUtc).getTime();
        }

        const file = new File([blob], finalName, fileOptions);
        return file;
      } catch (error) {
        ULogger.error("Error downloading file:", error);
        return null;
      }
    },
    [],
  );

  useEffect(() => {
    if (isPicking) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isPicking]);

  const openPicker = useCallback(() => {
    if (!isScriptLoaded || !window.google || !window.gapi) {
      ULogger.warn("Google Drive scripts are not loaded yet.");
      return;
    }

    if (!clientId || !apiKey) {
      ULogger.warn("Google API Key or Client ID is missing.");
      return;
    }

    window.scrollTo(0, 0);
    setIsPicking(true);

    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: "https://www.googleapis.com/auth/drive.file",
      callback: async (response: any) => {
        if (response.error !== undefined) {
          setIsPicking(false);
          throw response;
        }

        const accessToken = response.access_token;

        const createPicker = () => {
          const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS);
          // Allow audio and video mime types
          view.setMimeTypes("audio/mpeg,audio/wav,audio/m4a,video/mp4,video/webm");

          const picker = new window.google.picker.PickerBuilder()
            .addView(view)
            .setOAuthToken(accessToken)
            .setDeveloperKey(apiKey)
            .setCallback(async (data: any) => {
              if (data.action === window.google.picker.Action.PICKED) {
                // The popup automatically closes here, so we restore scrolling immediately
                // before the potentially long downloadFile await.
                document.body.style.overflow = "";

                const doc = data.docs[0];
                const fileId = doc.id;
                const fileName = doc.name;
                const mimeType = doc.mimeType;
                const lastEditedUtc = doc.lastEditedUtc; // Google Picker provides this

                try {
                  const file = await downloadFile(
                    fileId,
                    fileName,
                    mimeType,
                    accessToken,
                    lastEditedUtc,
                  );
                  if (file) {
                    if (!UFile.isCallFile(file)) {
                      onError?.(TKey.CALL_ANALYSIS_UPLOAD_ERROR_FORMAT);
                    } else if (file.size > UFile.getMaxCallFileSize()) {
                      onError?.(TKey.CALL_ANALYSIS_UPLOAD_ERROR_SIZE);
                    } else {
                      onFilesSelected([file]);
                    }
                  } else {
                    onError?.(TKey.CALL_ANALYSIS_DRIVE_ERROR_DOWNLOAD);
                  }
                } catch (_err: any) {
                  onError?.(TKey.CALL_ANALYSIS_DRIVE_ERROR_DOWNLOAD);
                }

                setIsPicking(false);
              } else if (
                data.action === window.google.picker.Action.CANCEL ||
                data.action === window.google.picker.Action.ERROR ||
                data.action === "error"
              ) {
                setIsPicking(false);
              }
            })
            .build();

          picker.setVisible(true);
        };

        createPicker();
      },
      error_callback: (error: any) => {
        ULogger.error("Google OAuth error:", error);
        setIsPicking(false);
      },
    });

    tokenClient.requestAccessToken({ prompt: "" });
  }, [isScriptLoaded, clientId, apiKey, onFilesSelected, onError, downloadFile]);

  return { openPicker, isScriptLoaded, isPicking };
}
